import React, { useState, useRef, useEffect } from 'react';
import { useGameStore } from '../store/gameStore';
import { HexMap } from './HexMap';
import { TheaterHeader } from './TheaterHeader';
import { ContextMenu } from './ContextMenu';
import { ActionAnimations } from './ActionAnimations';
import { MultiplayerChat } from './MultiplayerChat';
import { Plus, Minus, Move } from 'lucide-react';

export const CityMap: React.FC = () => {
  const { selectSector } = useGameStore();
  
  // Viewport State
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [lastMousePos, setLastMousePos] = useState({ x: 0, y: 0 });
  
  // Context Menu State
  const [contextMenu, setContextMenu] = useState<{ visible: boolean; x: number; y: number; sectorId: string } | null>(null);
  const [animatingAction, setAnimatingAction] = useState<{ type: string; x: number; y: number } | null>(null);

  const containerRef = useRef<HTMLDivElement>(null);

  // Zoom Handler (Scroll Wheel)
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const scaleAmount = -e.deltaY * 0.001;
      setZoom(z => Math.min(Math.max(0.5, z + scaleAmount), 2));
    };

    container.addEventListener('wheel', onWheel, { passive: false });
    return () => container.removeEventListener('wheel', onWheel);
  }, []);

  // Pan Handlers
  const handleMouseDown = (e: React.MouseEvent) => {
    if (e.button === 0) { // Left click only
      setIsDragging(true);
      setLastMousePos({ x: e.clientX, y: e.clientY });
    }
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (isDragging) {
      const dx = e.clientX - lastMousePos.x;
      const dy = e.clientY - lastMousePos.y;
      setPan(p => ({ x: p.x + dx, y: p.y + dy }));
      setLastMousePos({ x: e.clientX, y: e.clientY });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // Context Menu Handler
  const handleContextMenu = (e: React.MouseEvent, sectorId: string) => {
    e.preventDefault();
    setContextMenu({
      visible: true,
      x: e.clientX,
      y: e.clientY,
      sectorId
    });
  };

  const handleCloseContextMenu = () => {
    setContextMenu(null);
  };

  const triggerAnimation = (type: string, x?: number, y?: number) => {
    setAnimatingAction({ 
      type, 
      x: x || window.innerWidth / 2, 
      y: y || window.innerHeight / 2 
    });
    setTimeout(() => setAnimatingAction(null), 2500);
  };

  return (
    <div className="relative w-full h-full overflow-hidden bg-slate-950 select-none">
      {/* Header - Fixed to top, high z-index */}
      <div className="absolute top-0 left-0 w-full z-[100] pointer-events-none">
        <div className="pointer-events-auto">
          <TheaterHeader />
        </div>
      </div>

      {/* Map Container */}
      <div 
        ref={containerRef}
        className="w-full h-full cursor-move relative z-0 overflow-hidden"
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
      >
        {/* Transform Layer */}
        <div 
          className="absolute inset-0 transition-transform duration-75 ease-out origin-center flex items-center justify-center"
          style={{ 
            transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})` 
          }}
        >
          <HexMap 
            onSectorSelect={selectSector} 
            onContextMenu={handleContextMenu}
          />
        </div>
      </div>

      {/* HUD Controls */}
      <div className="absolute bottom-8 right-8 flex flex-col gap-2 z-[90]">
        <div className="bg-slate-900/90 border border-slate-700 rounded-lg p-2 shadow-xl backdrop-blur-sm flex flex-col gap-2">
          <button 
            onClick={() => setZoom(z => Math.min(z + 0.2, 2))}
            className="p-2 hover:bg-slate-800 rounded text-slate-400 hover:text-white transition-colors"
            title="Zoom In"
          >
            <Plus className="w-5 h-5" />
          </button>
          <div className="h-px bg-slate-800" />
          <button 
            onClick={() => setZoom(z => Math.max(0.5, z - 0.2))}
            className="p-2 hover:bg-slate-800 rounded text-slate-400 hover:text-white transition-colors"
            title="Zoom Out"
          >
            <Minus className="w-5 h-5" />
          </button>
          <div className="h-px bg-slate-800" />
          <button 
            onClick={() => { setZoom(1); setPan({ x: 0, y: 0 }); }}
            className="p-2 hover:bg-slate-800 rounded text-slate-400 hover:text-white transition-colors"
            title="Reset View"
          >
            <Move className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Secure Comms (Chat) */}
      <div className="absolute bottom-4 right-20 z-[90] pointer-events-none">
        <div className="pointer-events-auto">
          <MultiplayerChat />
        </div>
      </div>

      {/* Context Menu */}
      {contextMenu && (
        <ContextMenu 
          x={contextMenu.x} 
          y={contextMenu.y} 
          sectorId={contextMenu.sectorId} 
          onClose={handleCloseContextMenu}
          triggerAnimation={(type) => triggerAnimation(type, contextMenu.x, contextMenu.y)}
        />
      )}

      {/* Animations Overlay */}
      {animatingAction && (
        <ActionAnimations 
          type={animatingAction.type} 
          x={animatingAction.x} 
          y={animatingAction.y} 
        />
      )}
    </div>
  );
};